import React, { useMemo } from 'react'

import View from '../../../../../components/pdf/View'
import Text from '../../../../../components/pdf/Text'
import Separator from '../../../../../components/pdf/Separator'
import { Table, TableCol, TableRow } from '../../../../../components/pdf/Table'
import { TableColText, TableHeadText, TableRowLabelValue } from '../../../../../components/pdf/TableHelpers'
import {
  findReplyByQuestionId,
  getReplyValue,
  toDateFormat,
  calcCompliances,
  findReplyByParentReference,
  CONSTANTS
} from '../../_helpers/pdfHelpers'
import { useServiceOrder } from '../../_load/ServiceOrderLoader'
import Grid from '../../../../../components/pdf/Grid'

const weights = {
  'Inspeção de Campo': 10,
  'Disposições Gerais': 10,
  'EPI (Ficha) e Ensaios Elétricos': 6,
  'Veículo, Ferramental/Doc': 8,
  CIPA: 8,
  Treinamentos: 8,
  APR: 6,
  'Prevenção e Combate a Incêndio': 8,
  'Documentação de Segurança': 8,
  CEA: 5,
  Saúde: 8,
  DESC: 5,
  'Ambiente de Trabalho': 10
}

const replyIdsByCategory = {
  'Inspeção de Campo': [8995, 8997, 9309],
  'Disposições Gerais': [9009, 9312, 9314, 9316, 9318, 9320],
  'EPI (Ficha) e Ensaios Elétricos': [9029, 9325, 9327, 9329, 9331, 9323],
  'Veículo, Ferramental/Doc': [
    9049, 9334, 9336, 9338, 9340, 9342, 9344, 9346, 9348, 9350, 9352, 9354, 9356, 9358, 9360, 9362, 9364, 9366, 9368,
    9370
  ],
  CIPA: [
    9095, 9373, 9373, 9375, 9377, 9379, 9381, 9383, 9385, 9387, 9389, 9391, 9393, 9395, 9397, 9399, 9401, 9403, 9405,
    9407, 9409, 9411, 9413
  ],
  Treinamentos: [9147, 9416, 9418, 9420, 9422, 9424, 9426],
  APR: [9169, 9429, 9431],
  'Prevenção e Combate a Incêndio': [9183, 9434, 9436, 9438, 9440, 9442, 9444, 9446, 9448, 9450, 9452, 9454],
  'Documentação de Segurança': [9215, 9457, 9459, 9461, 9463, 9465, 9467, 9469, 9471, 9473, 9475, 9477],
  CEA: [9248, 9480, 9482, 9484, 9486],
  Saúde: [9266],
  DESC: [9276, 9278],
  'Ambiente de Trabalho': [9287, 9491, 9493, 9495, 9497, 9499, 9501, 9503, 9505, 9507]
}

const RequirementReplies = ({ replyIds }) => {
  const { replies } = useServiceOrder()
  return (
    <>
      <Table width="100%" style={{ pageBreakInside: 'auto', pageBreakAfter: 'auto' }}>
        {replyIds.map((replyId, index) => {
          const reply = findReplyByQuestionId(replies, replyId)
          const replyValue = getReplyValue(reply).trim()
          const status =
            replyValue === CONSTANTS.ATENDE
              ? CONSTANTS.ATENDE
              : replyValue === CONSTANTS.NAO_ATENDE
              ? CONSTANTS.NAO_ATENDE
              : CONSTANTS.NA
          const statusColor =
            replyValue === CONSTANTS.ATENDE ? '#a8d08d' : replyValue === CONSTANTS.NAO_ATENDE ? '#ff5050' : '#5b9bd5'

          const obs =
            status === CONSTANTS.NAO_ATENDE
              ? getReplyValue(findReplyByParentReference(replies, reply.question.reference))
              : ''
          const verification = reply.question.name
          const requirement = reply.question?.description?.replace('Requisito:', '') || '-'
          return (
            <TableRow key={replyId}>
              <Table width={'100%'} color={'transparent'} style={{ border: '0px' }}>
                <TableCol style={{ verticalAlign: 'middle', borderColor: 'transparent' }} align="center" width={'26px'}>
                  <Text bold align={'center'}>
                    {index + 1}
                  </Text>
                </TableCol>
                <TableCol>
                  <TableRowLabelValue borderColor={'transparent'} label={'Requisito'} value={requirement} />
                  <TableRowLabelValue
                    colTextAlign={'middle'}
                    textAlign="center"
                    borderColor={'transparent'}
                    label={'Verificação'}
                    value={verification}
                  />
                  <TableRowLabelValue borderColor={'transparent'} label={'Observação'} value={obs} />
                </TableCol>
                <TableCol style={{ verticalAlign: 'middle', width: '60px' }} align="center" bgColor={statusColor}>
                  <Text bold align={'center'}>
                    {status}
                  </Text>
                </TableCol>
              </Table>
            </TableRow>
          )
        })}
      </Table>
    </>
  )
}

const Requirement = ({ replies, categoryName, verificationReply, sampleReply, obsReply, requirements, replyIds }) => {
  const repliesByCategory = replies[categoryName]

  const statusResult = useMemo(() => {
    if (!repliesByCategory?.length || !replyIds.length) return ''

    const tmpReplies = repliesByCategory.filter(reply => replyIds.includes(reply.question.id))
    const result = calcCompliances(tmpReplies)
    return result
  }, [repliesByCategory, replyIds])

  const nonCompliances = useMemo(() => {
    const replies = repliesByCategory.filter(reply => replyIds.includes(reply.question.id))
    const NAs = replies.filter(reply => !getReplyValue(reply))
    const repliesWithouNAs = replies.filter(el => !NAs.includes(el))
    const nonCompliances = repliesWithouNAs.filter(reply => getReplyValue(reply).trim() === CONSTANTS.NAO_ATENDE)
    return nonCompliances.length
  }, [repliesByCategory, replyIds])

  return (
    <>
      <Table width="100%">
        <TableRow>
          <TableHeadText bgColor={'#e2efd9'} align="center">
            Requisito
          </TableHeadText>
          <TableHeadText bgColor={'#e2efd9'} align="center">
            Peso
          </TableHeadText>
          <TableHeadText bgColor={'#e2efd9'} align="center">
            Verificação
          </TableHeadText>
          <TableHeadText bgColor={'#e2efd9'} align="center">
            Amostra
          </TableHeadText>
          <TableHeadText bgColor={'#e2efd9'} align="center">
            Observação
          </TableHeadText>
          <TableHeadText bgColor={'#e2efd9'} align="center">
            Nº não conformidades
          </TableHeadText>
          <TableHeadText bgColor={'#e2efd9'} align="center">
            Avaliação
          </TableHeadText>
        </TableRow>
        <TableRow>
          <TableColText bold bgColor={'#ed7d31'} align="center">
            {categoryName}
          </TableColText>
          <TableColText bgColor={'#ed7d31'} align="center">
            {weights[categoryName]}%
          </TableColText>
          <TableColText bgColor={'#ed7d31'} align="center">
            {verificationReply}
          </TableColText>
          <TableColText bgColor={'#ed7d31'} align="center">
            {sampleReply}
          </TableColText>
          <TableColText bgColor={'#ed7d31'} align="center">
            {obsReply}
          </TableColText>
          <TableColText bgColor={'#ed7d31'} align="center">
            {nonCompliances}
          </TableColText>
          <TableColText
            bgColor={
              statusResult === CONSTANTS.NA ? '#5b9bd5' : statusResult === CONSTANTS.NAO_ATENDE ? '#ff5050' : '#a8d08d'
            }
            align="center"
            bold
          >
            {statusResult}
          </TableColText>
        </TableRow>
        <TableRow>
          <TableColText colSpan={7} align="left">
            Premissas do requisito:
          </TableColText>
        </TableRow>
        <TableRow>
          <TableColText colSpan={7} align="left">
            {requirements}
          </TableColText>
        </TableRow>
      </Table>
      <RequirementReplies replyIds={replyIds} />
      <Separator weight={4} />
    </>
  )
}

const Content = () => {
  const { replies } = useServiceOrder()
  const unit = getReplyValue(findReplyByQuestionId(replies, 8982))
  const unitResponsible = getReplyValue(findReplyByQuestionId(replies, 8983))
  const agent = getReplyValue(findReplyByQuestionId(replies, 8984))
  const date = toDateFormat(new Date(), 'DD/MM/YYYY')

  const repliesByCategory = useMemo(() => {
    const result = {}
    replies.forEach(reply => {
      const {
        question: { category }
      } = reply
      const categoryName = category.name.trim()
      if (!result[categoryName]) {
        result[categoryName] = []
      }
      result[categoryName].push(reply)
    })

    return result
  }, [replies])

  const finalScore = useMemo(() => {
    let finalResult = 0
    Object.keys(weights).forEach(categoryName => {
      const replyIds = replyIdsByCategory[categoryName]
      const tmpReplies = replies.filter(reply => replyIds.includes(reply.question.id))

      const result = calcCompliances(tmpReplies)
      if (result === CONSTANTS.ATENDE) finalResult += weights[categoryName]
      if (result === CONSTANTS.ATENDE_PARCIALMENTE) finalResult += weights[categoryName] / 2
    })
    return finalResult
  }, [replies])

  return (
    <View pageBreakAfter>
      <Text size={'small'}>
        <Grid>
          <Table width="100%">
            <tr style={{ border: '1px solid black' }}>
              <td style={{ width: '141px' }}>
                <Text align={'center'} bold>
                  Empresa:
                </Text>
              </td>
              <td>
                <Text>CARAMURU CONSTRUÇÕES LTDA</Text>
              </td>
              <td style={{ borderLeft: '1px solid black' }}>
                <Text align={'center'}>RESULTADO FINAL</Text>
              </td>
            </tr>
            <tr style={{ border: '1px solid black' }}>
              <td style={{ width: '141px' }}>
                <Text align={'center'} bold>
                  Unidade:
                </Text>
              </td>
              <td>
                <Text>{unit}</Text>
              </td>
              <td rowSpan={4} style={{ borderLeft: '1px solid black' }}>
                <Text align={'center'} size={'h1'}>
                  {finalScore}%
                </Text>
              </td>
            </tr>
            <tr style={{ border: '1px solid black' }}>
              <td style={{ width: '141px' }}>
                <Text align={'center'} bold>
                  Responsável unidade:
                </Text>
              </td>
              <td>
                <Text>{unitResponsible}</Text>
              </td>
            </tr>
            <tr style={{ border: '1px solid black' }}>
              <td style={{ width: '141px' }}>
                <Text align={'center'} bold>
                  Agente verificação:
                </Text>
              </td>
              <td>
                <Text>{agent}</Text>
              </td>
            </tr>
            <tr style={{ border: '1px solid black' }}>
              <td style={{ width: '141px' }}>
                <Text align={'center'} bold>
                  Data:
                </Text>
              </td>
              <td>
                <Text>{date}</Text>
              </td>
            </tr>
          </Table>
        </Grid>
        <Separator weight={4} />
        <Requirement
          replies={repliesByCategory}
          verificationReply={'Conforme check-list de inspeção (GIFS / BBS)'}
          sampleReply={'2 Equipes'}
          obsReply={getReplyValue(findReplyByQuestionId(replies, 9509))}
          categoryName={'Inspeção de Campo'}
          requirements={`Deverão ser inspecionadas 2 equipes da unidade
 O item deverá ser considerado como "Atende" se todos os itens inspecionados estejam em conformidade, do contrário deverá ser considerado "Não Atende"`}
          replyIds={replyIdsByCategory['Inspeção de Campo']}
        />
        <Requirement
          replies={repliesByCategory}
          verificationReply={'NRs'}
          sampleReply={'10% dos colaboradores da UTD, limitado ao máximo de 10 colaboradores'}
          obsReply={getReplyValue(findReplyByQuestionId(replies, 9510))}
          categoryName={'Disposições Gerais'}
          requirements={`Deverão ser inspecionadas conforme amostra da documentação de verificação de EPI
          O item deverá ser considerado como "Atende" se todos os itens inspecionados estejam em conformidade, do contrário deverá ser considerado "Não Atende"`}
          replyIds={replyIdsByCategory['Disposições Gerais']}
        />
        <Requirement
          replies={repliesByCategory}
          verificationReply={'Conforme manual de verificação 01'}
          sampleReply={'10% dos colaboradores da UTD, limitado ao máximo de 7 colaboradores'}
          obsReply={getReplyValue(findReplyByQuestionId(replies, 9511))}
          categoryName={'EPI (Ficha) e Ensaios Elétricos'}
          requirements={`Deverão ser inspecionadas 10% das fichas de EPI dos colaboradores operacionais da unidade
          O item deverá ser considerado como "Atende" se todos os itens inspecionados estejam em conformidade, do contrário deverá ser considerado "Não Atende"`}
          replyIds={replyIdsByCategory['EPI (Ficha) e Ensaios Elétricos']}
        />
        <Requirement
          replies={repliesByCategory}
          verificationReply={'Conforme manual de verificação 02	'}
          sampleReply={'2 Veículos, sendo um veículo leve e um veículo pesado (guindauto/cesto aéreo)'}
          obsReply={getReplyValue(findReplyByQuestionId(replies, 9512))}
          categoryName={'Veículo, Ferramental/Doc'}
          requirements={`Deverão ser inspecionados dois veículos, sendo um deles um veículo leve e um veículo pesado (guindauto ou cesto aéreo).
          O item deverá ser considerado como "Atende" se todos os itens inspecionados estejam em conformidade, do contrário deverá ser considerado "Não Atende"`}
          replyIds={replyIdsByCategory['Veículo, Ferramental/Doc']}
        />
        <Requirement
          replies={repliesByCategory}
          verificationReply={'Conforme manual de verificação 03'}
          sampleReply={'CIPA DA LOCALIDADE'}
          obsReply={getReplyValue(findReplyByQuestionId(replies, 9513))}
          categoryName={'CIPA'}
          requirements={`Deverão ser verificados itens de atendimento a requisitos legais e orientações técnicas referentes à CIPA.`}
          replyIds={replyIdsByCategory['CIPA']}
        />
        <Requirement
          replies={repliesByCategory}
          verificationReply={'Conforme treinamentos'}
          sampleReply={
            '10% dos colaboradores da UTD, limitado ao máximo de 7 colaboradores (mesmo colaboradores analisados no ASO)'
          }
          obsReply={getReplyValue(findReplyByQuestionId(replies, 9514))}
          categoryName={'Treinamentos'}
          requirements={`Deverá ser verificada a participação em treinamentos. Os colaboradores analisados devem ser os mesmos analisados no item ASO.`}
          replyIds={replyIdsByCategory['Treinamentos']}
        />
        <Requirement
          replies={repliesByCategory}
          verificationReply={`Deve-se verificar as APR's das equipes inspecioandas em campo`}
          sampleReply={'2 Equipes'}
          obsReply={getReplyValue(findReplyByQuestionId(replies, 9515))}
          categoryName={'APR'}
          requirements={`Deverão ser inspecionadas as APR's realizadas pelas equipes inspecionadas em campo.`}
          replyIds={replyIdsByCategory['APR']}
        />
        <Requirement
          replies={repliesByCategory}
          verificationReply={'Conforme manual de verificação 04'}
          sampleReply={'BRIGADA DA LOCALIDADE'}
          obsReply={getReplyValue(findReplyByQuestionId(replies, 9516))}
          categoryName={'Prevenção e Combate a Incêndio'}
          requirements={`Deverão ser analisados o dimensionamento, atestados, atas de reunião, simulados e checklist's referentes à brigada de emergência.`}
          replyIds={replyIdsByCategory['Prevenção e Combate a Incêndio']}
        />
        <Requirement
          replies={repliesByCategory}
          verificationReply={'Conforme manual de verificação 05	'}
          sampleReply={'PPRA, PCMSO E MAPA DE RISCO DA LOCALIDADE'}
          obsReply={getReplyValue(findReplyByQuestionId(replies, 9517))}
          categoryName={'Documentação de Segurança'}
          requirements={`Deverão ser inspecionados o PPRA, o PCMSO, o mapa de risco e o livro de registros (M.T.E) da localidade.`}
          replyIds={replyIdsByCategory['Documentação de Segurança']}
        />
        <Requirement
          replies={repliesByCategory}
          verificationReply={'Conforme manual de verificação 05	'}
          sampleReply={"5 CEA's da UTD (preferencialment os de maior gravidade)"}
          obsReply={getReplyValue(findReplyByQuestionId(replies, 9518))}
          categoryName={'CEA'}
          requirements={`Deverão ser analisados todos os CEA's ocorridos na localidade no último trimestre.`}
          replyIds={replyIdsByCategory['CEA']}
        />
        <Requirement
          replies={repliesByCategory}
          verificationReply={'Verificação do ASO e condição de restrição'}
          sampleReply={'10% dos colaboradores da UTD, limitado ao máximo de 7 colaboradores'}
          obsReply={getReplyValue(findReplyByQuestionId(replies, 9519))}
          categoryName={'Saúde'}
          requirements={`Deverão ser inspecionados o ASO e a condição de restrição dos colaboradores inspecionados em campo`}
          replyIds={replyIdsByCategory['Saúde']}
        />
        <Requirement
          replies={repliesByCategory}
          verificationReply={"Verificação de temas e presença em DESC's	"}
          sampleReply={"4 DESC's"}
          obsReply={getReplyValue(findReplyByQuestionId(replies, 9520))}
          categoryName={'DESC'}
          requirements={`Deverão ser analisados os temas e listas de presença dos DESC's.`}
          replyIds={replyIdsByCategory['DESC']}
        />
        <Requirement
          replies={repliesByCategory}
          verificationReply={'Verificação das condições do ambiente de trabalho	'}
          sampleReply={'UNIDADE'}
          obsReply={getReplyValue(findReplyByQuestionId(replies, 9521))}
          categoryName={'Ambiente de Trabalho'}
          requirements={''}
          replyIds={replyIdsByCategory['Ambiente de Trabalho']}
        />
      </Text>
    </View>
  )
}

export default Content
